import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import './AccountShell.css';
import './PrivacyPage.css';

// Stable English identifiers — used for `data-section`, slug() anchors, and the
// active-section state. These are logic keys, NOT display copy; their visible
// labels come from account:privacy.sections.<key>. Do not translate these.
const SECTIONS = [
  'Overview',
  'Information We Collect',
  'Connected Services',
  'How We Use Your Information',
  'How We Share Your Information',
  'Data Retention',
  'Your Privacy Rights',
  'California Residents (CCPA/CPRA)',
  'Cookies & Local Storage',
  'Data Security',
  'Changes to This Policy',
  'Contact Us',
] as const;

type SectionId = (typeof SECTIONS)[number];

// Maps each stable SectionId to its i18n label key under account:privacy.sections.
const SECTION_LABEL_KEYS: Record<SectionId, string> = {
  'Overview': 'overview',
  'Information We Collect': 'informationWeCollect',
  'Connected Services': 'connectedServices',
  'How We Use Your Information': 'howWeUse',
  'How We Share Your Information': 'howWeShare',
  'Data Retention': 'dataRetention',
  'Your Privacy Rights': 'yourRights',
  'California Residents (CCPA/CPRA)': 'california',
  'Cookies & Local Storage': 'cookies',
  'Data Security': 'security',
  'Changes to This Policy': 'changes',
  'Contact Us': 'contact',
};

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const PrivacyPage: React.FC = () => {
  const { t } = useTranslation(['account']);
  const [activeSection, setActiveSection] = useState<SectionId>(SECTIONS[0]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const headings = container.querySelectorAll<HTMLElement>('[data-section]');
    for (const h of headings) {
      if (h.offsetTop - container.scrollTop < 120) {
        setActiveSection(h.dataset.section as SectionId);
      }
    }
  };

  const scrollTo = (section: SectionId) => {
    const el = document.querySelector<HTMLElement>(`[data-section="${section}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(section);
  };

  return (
    <div className="privacy-page" onScroll={handleScroll}>
      {/* ── Sidebar TOC ─────────────────────────────────────────────── */}
      <nav className="privacy-toc" aria-label={t('account:privacy.toc_aria')}>
        <div className="privacy-toc-label">{t('account:privacy.tocLabel')}</div>
        {SECTIONS.map((s) => (
          <button
            key={s}
            className={`privacy-toc-item${activeSection === s ? ' active' : ''}`}
            onClick={() => scrollTo(s)}
          >
            {t(`account:privacy.sections.${SECTION_LABEL_KEYS[s]}`)}
          </button>
        ))}
        <div className="privacy-toc-updated">
          <Trans i18nKey="account:privacy.lastUpdated" components={{ br: <br /> }} />
        </div>
      </nav>

      {/* ── Main content ─────────────────────────────────────────────── */}
      <main className="privacy-main" onScroll={handleScroll}>
        <div className="privacy-body">

          {/* ── Overview ──────────────────────────────────────────────── */}
          <section id={slug('Overview')}>
            <h1 data-section="Overview">{t('account:privacy.tocLabel')}</h1>
            <p className="privacy-lead">
              Rhemn ("<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>") operates the Rhemn browser extension and related web properties (collectively, the "<strong>Service</strong>"). This Privacy Policy explains what personal information we collect, how we use it, and your rights regarding that information.
            </p>
            <p>
              By using the Service you agree to the practices described here. If you do not agree, please discontinue use and uninstall the extension.
            </p>
            <div className="privacy-callout privacy-callout--green">
              <strong>We do not sell, rent, or trade your personal information to any third party for money or other valuable consideration.</strong> We do not share your data for cross-context behavioral advertising.
            </div>
          </section>

          {/* ── Information We Collect ────────────────────────────────── */}
          <section id={slug('Information We Collect')}>
            <h2 data-section="Information We Collect">{t('account:privacy.headings.informationWeCollect')}</h2>

            <h3>1. Account Information</h3>
            <p>When you sign in with Google or Apple we receive:</p>
            <ul>
              <li><strong>Display name</strong> — provided by your identity provider</li>
              <li><strong>Email address</strong> — used to identify your account. We may also email you about product updates and new features; you can unsubscribe from these at any time without affecting your account or billing emails.</li>
              <li><strong>Profile photo URL</strong> — provided by your identity provider</li>
              <li><strong>Authentication tokens</strong> — short-lived Firebase ID tokens used to verify your identity on our servers; never stored long-term in plaintext</li>
            </ul>
            <p>We do <em>not</em> receive or store your Google or Apple password.</p>

            <h3>2. User-Created Content</h3>
            <p>The following content is created by you and stored so it syncs across your devices:</p>
            <ul>
              <li>Tasks (to-dos) and task lists</li>
              <li>Notes (text content, title, tags)</li>
              <li>Focus intentions you type into the focus bar</li>
              <li>Saved ("favorited") Quran verses</li>
              <li>App preferences (weather unit, prayer calculation method, display settings)</li>
            </ul>

            <h3>3. Voice Notes &amp; AI Features</h3>
            <p>
              When you record a <strong>voice note</strong>, audio from your microphone is captured <em>only while you are actively recording</em> and is sent — by way of our backend — to our speech-to-text provider (<strong>OpenAI</strong>) to generate a written transcript. We use the audio solely to produce that transcript and do not retain the audio recording after transcription completes.
            </p>
            <p>
              When you use <strong>AI features</strong>, the relevant text is sent to a third-party AI provider to generate the response. It is processed only to return your result; our providers do not use it to train their models. Different features use different providers:
            </p>
            <ul>
              <li>
                <strong>Note AI features</strong> — note summaries, voice-note summaries and structuring, and in-editor AI writing tools (continue, rewrite, summarize, translate, and similar) — send your <strong>note content or voice-note transcript to OpenAI</strong>, our default provider for these features. (Depending on our configuration, this note text may instead be processed by Anthropic.)
              </li>
              <li>
                <strong>Quran AI features</strong> — Quran insights, Ask, tafsir, related verses, and verse reflections — send the verse you are viewing (and, for reflections, the intention you enter) to <strong>Anthropic</strong>.
              </li>
              <li><strong>Task AI features</strong> — Plan my day, voice task capture, and smart task parsing — send <strong>the text and priority of the tasks involved</strong> (and anything you say or type for the feature) to <strong>Anthropic</strong>, together with your prayer-block times. These tasks may include tasks synced from a service you have connected (see <a href="#connected-services">Connected Services</a>). If you have connected a calendar, Plan my day also sends the <em>start and end times</em> of your busy periods — never event titles, descriptions, attendees, or locations.</li>
            </ul>

            <h3>4. Location Information</h3>
            <p>
              The weather widget and prayer times feature require your approximate location (latitude and longitude). Location coordinates are obtained via the browser Geolocation API <em>only when you enable these features</em> and are sent directly to our third-party data providers (OpenWeatherMap for weather; Aladhan for prayer times). <strong>We do not store your precise GPS coordinates on our servers.</strong> Your city-level location preference may be stored locally in the extension and in your account preferences.
            </p>

            <h3>5. Usage Analytics</h3>
            <p>
              We collect anonymous, aggregate usage analytics — for example, how many people opened a new tab, added a task, or shared a verse. These are stored only as daily totals tied to a random identifier held on your device; they are <em>never</em> linked to your identity, never include the content of your tasks, notes, or verses, and are never sold. This data stays on our own servers — we do <em>not</em> use any third-party analytics provider. You can turn this off any time with the "Anonymous analytics" toggle in the profile menu.
            </p>

            <h3>6. Subscription & Billing Information</h3>
            <p>
              If you subscribe to Rhemn Pro, payment is processed entirely by Stripe. We <em>never</em> see or store your full card number, CVV, or bank details. We retain your Stripe customer ID and subscription status (active / cancelled / trial) to unlock Pro features.
            </p>

            <h3>7. Technical Information</h3>
            <p>Our servers receive standard HTTP request information when the extension communicates with our backend, including your IP address. We do not use IP addresses for tracking or advertising; they may appear in server logs that are automatically purged after 30 days.</p>

            <h3>What we do NOT collect</h3>
            <ul>
              <li>The contents of your calendar events (see <a href="#connected-services">Connected Services</a>)</li>
              <li>Browsing history or URLs you visit in any tab</li>
              <li>Keystrokes, clipboard contents, or screenshots</li>
              <li>Data from any other browser extension</li>
              <li>Precise persistent location (only passed to weather / prayer time APIs per request)</li>
              <li>Information about other apps on your device</li>
              <li>Biometric data</li>
            </ul>
          </section>

          {/* ── Connected Services (mirrors privacy.html #connected-services) ── */}
          <section id={slug('Connected Services')}>
            <h2 data-section="Connected Services">{t('account:privacy.headings.connectedServices')}</h2>
            <p className="privacy-lead">Rhemn Pro lets you connect Google Calendar, Google Tasks, and Todoist, and — in the Rhemn app for iPhone, iPad, and Mac — Apple Calendar and Reminders. Connecting is always your choice, and you can disconnect at any time. This section explains exactly what Rhemn accesses, what it stores, and what it never does.</p>
            <div className="privacy-callout"><strong>Rhemn&apos;s use and transfer to any other app of information received from Google APIs will adhere to <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</strong></div>
            <p>In practice, this means that data we receive from Google:</p>
            <ul>
              <li>is used only to provide the Rhemn features you can see and choose to use, described below;</li>
              <li>is never sold, and never used for advertising, including personalized or retargeted advertising;</li>
              <li>is never used to determine credit-worthiness or for lending purposes, and never used to train generalized artificial-intelligence or machine-learning models;</li>
              <li>is transferred to others only when needed to provide those features (for example, the task text Plan my day sends to our AI provider, which does not use it to train its models), to comply with the law, or as part of a merger or acquisition with your consent — and is never transferred to data brokers;</li>
              <li>is never read by people, except with your explicit permission for a specific item (for example, when you ask us for support), when needed for security (such as investigating abuse), to comply with the law, or when the data has been aggregated and anonymized for internal operations.</li>
            </ul>
            <h3>Google Calendar and Google Tasks</h3>
            <p>When you connect Google, Rhemn asks for these permissions and uses each one only as described:</p>
            <table className="privacy-table">
              <thead>
                <tr>
                  <th>Permission (scope)</th>
                  <th>What Rhemn does with it</th>
                  <th>When it&apos;s requested</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>openid</code>, <code>email</code></td>
                  <td>Identify which Google account you connected and show its email address in Rhemn.</td>
                  <td>When you connect</td>
                </tr>
                <tr>
                  <td><code>https://www.googleapis.com/auth/tasks</code></td>
                  <td>Read and update the Google Tasks lists <em>you choose</em>, so tasks you add, edit, complete, or delete stay the same in Rhemn and in Google Tasks.</td>
                  <td>When you connect</td>
                </tr>
                <tr>
                  <td><code>https://www.googleapis.com/auth/calendar.calendarlist.readonly</code></td>
                  <td>List your calendars (names and colors) so you can choose which ones appear in Rhemn.</td>
                  <td>When you connect</td>
                </tr>
                <tr>
                  <td><code>https://www.googleapis.com/auth/calendar.events.readonly</code></td>
                  <td>Read the events on the calendars you choose, to show them inside your prayer blocks and to know when you are busy. Read-only: Rhemn cannot change or delete your events.</td>
                  <td>When you connect</td>
                </tr>
                <tr>
                  <td><code>https://www.googleapis.com/auth/calendar.app.created</code></td>
                  <td>Create one secondary calendar named &quot;Rhemn&quot; and add your prayer times to it. This permission only covers calendars Rhemn creates; it cannot touch your other calendars.</td>
                  <td>Only if you turn on &quot;Add prayer times to your calendar&quot; — requested separately, at that moment</td>
                </tr>
              </tbody>
            </table>
            <h3>Your calendar events are never stored</h3>
            <p>Rhemn fetches events from the calendars you select, for a window of at most three days, only when Rhemn needs to show them. <strong>Event contents are never saved to our database or any disk.</strong> To avoid asking Google repeatedly, our servers keep the latest result in memory for up to 3 minutes (and your list of calendar names and colors for up to 10 minutes), then discard it. The Rhemn app on your device may keep a copy of today&apos;s events so your day appears instantly; it is removed when you sign out. What we do store is your choice of which calendars to show.</p>
            <p>When you use Plan my day with a calendar connected, only the start and end times of your busy periods are used — never event titles, descriptions, attendees, or locations. If you choose to take notes for a meeting, that event&apos;s title is used as your note&apos;s title and is saved with your note like anything else you write.</p>
            <h3>What Rhemn writes to your Google Calendar</h3>
            <p>Only if you turn on &quot;Add prayer times to your calendar&quot;: Rhemn creates <strong>one</strong> secondary calendar named &quot;Rhemn&quot; and, for up to the next 7 days, adds five 20-minute events — one starting at each prayer time, marked as busy — named after the prayer (for example, &quot;Dhuhr&quot;), with the note &quot;Added by Rhemn. Prayer times for [your city].&quot; Rhemn never writes your coordinates, and never creates, changes, or deletes events on any other calendar. Turning the feature off lets you keep those events or remove the Rhemn calendar entirely.</p>
            <h3>Google Tasks and Todoist: what is stored</h3>
            <p>Two-way sync needs a copy, so for the task lists (Google Tasks) or projects (Todoist) <em>you choose to link</em>, Rhemn stores on our servers, in your Rhemn account:</p>
            <ul>
              <li>the list or project name;</li>
              <li>each task&apos;s text, completion status, due date, and whether it is a sub-task of another task;</li>
              <li>for Todoist, each task&apos;s priority;</li>
              <li>the identifiers Google or Todoist uses for those lists and tasks, so changes reach the right item;</li>
              <li>changes waiting to be sent to Google or Todoist (kept only until they are delivered).</li>
            </ul>
            <p>Lists and projects you do not link are never copied. Rhemn never deletes a list or project in Google Tasks or Todoist. It deletes an individual task there only when you delete that task in Rhemn.</p>
            <h3>Todoist</h3>
            <p>When you connect Todoist, Rhemn requests the <code>data:read_write</code> and <code>data:delete</code> permissions. It uses them to read your project list (so you can choose which to link), to read and update tasks in the projects you link, and to delete a task in Todoist when you delete it in Rhemn. Rhemn also reads your Todoist account ID and email address to identify the connection. What is stored is listed above.</p>
            <h3>Connection details we keep</h3>
            <p>For each connected Google or Todoist account we store:</p>
            <ul>
              <li>the account&apos;s email address and the provider&apos;s account ID;</li>
              <li>the permissions you granted;</li>
              <li>the access and refresh tokens that let Rhemn act on your behalf. These are <strong>encrypted at rest with AES-256-GCM</strong>, are never sent to your device, and never appear in our logs;</li>
              <li>sync bookkeeping: when the account last synced, a sync position marker, and — if you add prayer times to your calendar — a fingerprint of the last update, used only to avoid sending an identical update twice.</li>
            </ul>
            <h3>Apple Calendar and Reminders (iPhone, iPad, and Mac app)</h3>
            <p>In the Rhemn app for iPhone, iPad, and Mac you can let Rhemn use Apple Calendar and Reminders on that device. <strong>This data stays on your device and never reaches our servers.</strong> Rhemn reads it directly on the device to show your events and reminders, and writes changes back on the device. Syncing between your Apple devices is handled by Apple (iCloud), not by Rhemn. If you use Plan my day, only the start and end times of your busy periods are sent, as described above. You can turn this access off at any time in your device&apos;s Settings.</p>
            <h3>Disconnecting and revoking access</h3>
            <p>You can disconnect Google or Todoist at any time under <strong>Calendars & tasks</strong> — in the profile menu on desktop, or in the More tab in the iPhone and iPad app. When you do:</p>
            <ul>
              <li>if Rhemn created a &quot;Rhemn&quot; prayer-times calendar in your Google account, Rhemn deletes it first, so no stale prayer times are left behind;</li>
              <li>Rhemn asks Google or Todoist to revoke its access;</li>
              <li>the stored tokens and connection details are deleted immediately from our database;</li>
              <li>your linked lists either stay in Rhemn as ordinary Rhemn lists or are removed from Rhemn — your choice. Nothing is deleted in Google or Todoist.</li>
            </ul>
            <p>If Google or Todoist can&apos;t be reached at that moment, your tokens are still deleted from our database right away. The calendar or the access grant may then remain on their side until you remove it, as described below.</p>
            <p>You can also revoke Rhemn&apos;s access yourself from your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">Google Account permissions</a> page or from Todoist&apos;s settings (Integrations). Rhemn then stops syncing and asks you to reconnect.</p>
            <p><strong>Deleting your Rhemn account</strong> removes everything described in this section: Rhemn deletes its prayer-times calendar, asks each connected service to revoke its access, and deletes the stored tokens, connection details, and synced tasks along with the rest of your account.</p>
          </section>

          {/* ── How We Use ───────────────────────────────────────────── */}
          <section id={slug('How We Use Your Information')}>
            <h2 data-section="How We Use Your Information">{t('account:privacy.headings.howWeUse')}</h2>

            <table className="privacy-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Purpose</th>
                  <th>Legal basis (GDPR)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Account information</td>
                  <td>Authenticate you; display your name in greetings; link your data across devices</td>
                  <td>Contract performance</td>
                </tr>
                <tr>
                  <td>User-created content</td>
                  <td>Sync tasks, notes, and preferences to your account</td>
                  <td>Contract performance</td>
                </tr>
                <tr>
                  <td>Voice-note audio (transient)</td>
                  <td>Transcribe your recording into text via our speech-to-text provider</td>
                  <td>Consent (feature is opt-in)</td>
                </tr>
                <tr>
                  <td>Note / verse text (AI features)</td>
                  <td>Generate AI summaries, structured notes, and Quran insights</td>
                  <td>Consent / contract performance</td>
                </tr>
                <tr>
                  <td>Location (transient)</td>
                  <td>Fetch weather and calculate prayer times on demand</td>
                  <td>Consent (feature is opt-in)</td>
                </tr>
                <tr>
                  <td>Analytics events</td>
                  <td>Understand which features are used so we can improve the product</td>
                  <td>Consent (opt-in banner)</td>
                </tr>
                <tr>
                  <td>Connected services (Google Calendar, Google Tasks, Todoist)</td>
                  <td>Show your events in your day, keep the task lists you link in sync, and — if you turn it on — add prayer times to your Rhemn calendar</td>
                  <td>Consent (you choose to connect; disconnect any time)</td>
                </tr>
                <tr>
                  <td>Subscription status</td>
                  <td>Gate Pro features; manage billing relationship</td>
                  <td>Contract performance</td>
                </tr>
                <tr>
                  <td>Server logs / IP address</td>
                  <td>Security, abuse prevention, debugging</td>
                  <td>Legitimate interest</td>
                </tr>
              </tbody>
            </table>

            <p>We do not use your information for automated decision-making that produces legal or similarly significant effects.</p>
          </section>

          {/* ── How We Share ─────────────────────────────────────────── */}
          <section id={slug('How We Share Your Information')}>
            <h2 data-section="How We Share Your Information">{t('account:privacy.headings.howWeShare')}</h2>

            <p>We share data only as described below. We do not sell or share your data for advertising.</p>

            <h3>Service Providers</h3>
            <ul>
              <li>
                <strong>Google Firebase</strong> — authentication infrastructure.{' '}
                <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
              </li>
              <li>
                <strong>Stripe</strong> — payment processing. Stripe is PCI-DSS compliant and processes card data under its own privacy and security standards.{' '}
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
              </li>
              <li>
                <strong>OpenAI</strong> — speech-to-text transcription of voice notes, and the default provider for AI note features (note and voice-note summaries, note structuring, and in-editor AI writing tools). The audio you record, and the note content or transcript you submit to these features, is sent to OpenAI to generate the transcript or result; it is not used to train OpenAI's models.{' '}
                <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
              </li>
              <li><strong>Anthropic</strong> — AI Quran features (insights, Ask, tafsir, related verses, and verse reflections), task AI features (Plan my day, voice task capture, and smart task parsing), and AI note features where we have configured Anthropic as the note provider. The relevant text is sent to Anthropic to generate responses; it is not used to train Anthropic&apos;s models. <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">Privacy policy →</a></li>
              <li>
                <strong>OpenWeatherMap</strong> — your device's coordinates are sent per weather request.{' '}
                <a href="https://openweathermap.org/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
              </li>
              <li>
                <strong>Aladhan</strong> — your device's coordinates are sent per prayer-times request.{' '}
                <a href="https://aladhan.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
              </li>
              <li>
                <strong>Unsplash</strong> — anonymous requests for background photos.{' '}
                <a href="https://unsplash.com/privacy" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
              </li>
            </ul>

            <h3>Services You Connect</h3>
            <p>If you connect Google Calendar, Google Tasks, or Todoist, Rhemn exchanges data with that service only as you direct — to show your events, sync the lists you link, or add prayer times to your Rhemn calendar. See <a href="#connected-services">Connected Services</a> for exactly what is accessed and stored.</p>

            <h3>Legal Disclosures</h3>
            <p>We may disclose your information if required to do so by law, subpoena, or other legal process, or if we have a good-faith belief that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.</p>

            <h3>Business Transfers</h3>
            <p>If Rhemn is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you via the email address associated with your account before your information becomes subject to a different privacy policy.</p>
          </section>

          {/* ── Data Retention ───────────────────────────────────────── */}
          <section id={slug('Data Retention')}>
            <h2 data-section="Data Retention">{t('account:privacy.headings.dataRetention')}</h2>
            <ul>
              <li><strong>Account data & user content</strong> — retained for the lifetime of your account. Deleted within 30 days of an account-deletion request.</li>
              <li><strong>Server logs (including IP addresses)</strong> — automatically purged after 30 days.</li>
              <li><strong>Connected services</strong> — tokens and connection details are kept while an account is connected and deleted immediately when you disconnect or delete your Rhemn account. Synced tasks are kept while their list is linked. Calendar events are never stored; our servers hold them in memory for at most 3 minutes.</li>
              <li><strong>Analytics</strong> — stored on our own servers only as anonymous, aggregate daily counts (never individual event histories). Not linked to you and not sold.</li>
              <li><strong>Stripe records</strong> — Stripe retains transaction records as required by financial regulations (typically 7 years); we retain only subscription status.</li>
              <li><strong>Local extension data</strong> — stored in <code>chrome.storage.local</code> on your device and cleared when you uninstall the extension or use the in-app "Delete my data" function.</li>
            </ul>
          </section>

          {/* ── Your Rights ──────────────────────────────────────────── */}
          <section id={slug('Your Privacy Rights')}>
            <h2 data-section="Your Privacy Rights">{t('account:privacy.headings.yourRights')}</h2>
            <p>Depending on where you live, you may have some or all of the following rights. To exercise any of them, email <a href="mailto:hello@rhemn.com">hello@rhemn.com</a> from the address associated with your account. We will respond within 45 days (or the timeframe required by applicable law).</p>

            <div className="privacy-rights-grid">
              <div className="privacy-right-card">
                <div className="privacy-right-title">Access</div>
                <p>Request a copy of the personal information we hold about you.</p>
              </div>
              <div className="privacy-right-card">
                <div className="privacy-right-title">Correction</div>
                <p>Ask us to fix inaccurate or incomplete information.</p>
              </div>
              <div className="privacy-right-card">
                <div className="privacy-right-title">Deletion</div>
                <p>Request deletion of your account and all associated personal data.</p>
              </div>
              <div className="privacy-right-card">
                <div className="privacy-right-title">Portability</div>
                <p>Receive a machine-readable export of your todos, notes, and preferences.</p>
              </div>
              <div className="privacy-right-card">
                <div className="privacy-right-title">Opt-out of analytics</div>
                <p>Withdraw analytics consent at any time in Settings → Privacy.</p>
              </div>
              <div className="privacy-right-card">
                <div className="privacy-right-title">Non-discrimination</div>
                <p>We will not deny service, charge different prices, or provide a different level of service because you exercised a privacy right.</p>
              </div>
            </div>

            <h3>State-Specific Rights</h3>

            <details className="privacy-details">
              <summary>Virginia (VCDPA), Colorado (CPA), Connecticut (CTDPA), Utah (UCPA), Texas (TDPSA)</summary>
              <p>Residents of Virginia, Colorado, Connecticut, Utah, and Texas have the right to: access personal data we process about you; correct inaccuracies; delete personal data you have provided; obtain a portable copy of your data; and opt out of the processing of personal data for purposes of targeted advertising or the sale of personal data (neither of which we do). To appeal a decision regarding your request, reply to our response email within 60 days.</p>
            </details>

            <details className="privacy-details">
              <summary>European Union & United Kingdom (GDPR / UK GDPR)</summary>
              <p>If you are located in the EU or UK, you have the rights described above plus the right to object to processing based on legitimate interest and the right to lodge a complaint with your local supervisory authority. Our lawful bases for processing are described in the "How We Use Your Information" table above. We process EU/UK data on servers located in the United States; where required we rely on Standard Contractual Clauses for cross-border transfers.</p>
            </details>

            <details className="privacy-details">
              <summary>Canada (PIPEDA / Law 25)</summary>
              <p>Canadian residents may request access to or correction of their personal information, and may withdraw consent where processing is consent-based, subject to legal and contractual restrictions. Québec residents have additional rights under Law 25, including the right to data portability and the right to be forgotten.</p>
            </details>
          </section>

          {/* ── California ───────────────────────────────────────────── */}
          <section id={slug('California Residents (CCPA/CPRA)')}>
            <h2 data-section="California Residents (CCPA/CPRA)">{t('account:privacy.headings.california')}</h2>

            <p>This section applies to California residents and supplements the rest of this Policy.</p>

            <div className="privacy-callout privacy-callout--green">
              <strong>We do not sell your personal information.</strong> We do not share your personal information with third parties for cross-context behavioral advertising. You therefore have no opt-out right to exercise with respect to sale or sharing — because we do not engage in those activities.
            </div>

            <h3>Categories of Personal Information Collected</h3>
            <table className="privacy-table">
              <thead>
                <tr>
                  <th>Category (Cal. Civ. Code § 1798.140)</th>
                  <th>Examples</th>
                  <th>Collected?</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Identifiers</td>
                  <td>Name, email address</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <td>Personal information (Cal. Civ. Code § 1798.80)</td>
                  <td>Name, email</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <td>Protected classification characteristics</td>
                  <td>Religion inferred from use of Islamic features</td>
                  <td>No — we do not infer or store this</td>
                </tr>
                <tr>
                  <td>Commercial information</td>
                  <td>Subscription status</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <td>Internet / network activity</td>
                  <td>IP address in server logs</td>
                  <td>Yes (purged after 30 days)</td>
                </tr>
                <tr>
                  <td>Geolocation data</td>
                  <td>City-level location for weather/prayer times</td>
                  <td>Transient — passed to APIs, not stored on our servers</td>
                </tr>
                <tr>
                  <td>Inferences / profiles</td>
                  <td>—</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Sensitive personal information</td>
                  <td>—</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Biometric information</td>
                  <td>—</td>
                  <td>No</td>
                </tr>
              </tbody>
            </table>

            <h3>Your California Rights</h3>
            <ul>
              <li><strong>Right to Know</strong> — request disclosure of the categories and specific pieces of personal information we have collected about you.</li>
              <li><strong>Right to Delete</strong> — request deletion of your personal information, subject to certain exceptions.</li>
              <li><strong>Right to Correct</strong> — request correction of inaccurate personal information.</li>
              <li><strong>Right to Opt-Out of Sale/Sharing</strong> — we do not sell or share your data, so this right is not applicable.</li>
              <li><strong>Right to Limit Use of Sensitive Personal Information</strong> — we do not process sensitive personal information beyond what is necessary to provide the Service.</li>
              <li><strong>Right to Non-Discrimination</strong> — we will not discriminate against you for exercising any of your rights.</li>
            </ul>
            <p>To submit a verifiable consumer request, email <a href="mailto:hello@rhemn.com">hello@rhemn.com</a>. We will acknowledge your request within 10 business days and respond within 45 calendar days. We do not charge a fee unless requests are manifestly unfounded or excessive. We will verify your identity by confirming the email address on your account.</p>

            <p>You may designate an authorized agent to submit a request on your behalf by providing written authorization.</p>

            <p><strong>Shine the Light (Cal. Civ. Code § 1798.83):</strong> We do not disclose personal information to third parties for their direct marketing purposes, so no disclosure is required under this statute.</p>
          </section>

          {/* ── Cookies & Storage ────────────────────────────────────── */}
          <section id={slug('Cookies & Local Storage')}>
            <h2 data-section="Cookies & Local Storage">{t('account:privacy.headings.cookies')}</h2>
            <p>As a browser extension, Rhemn does not use traditional browser cookies in your web sessions. Instead, we use:</p>
            <ul>
              <li><strong><code>chrome.storage.local</code></strong> — stores your todos, notes, preferences, and auth state locally on your device. This data is not accessible to websites you visit.</li>
              <li><strong><code>localStorage</code></strong> — stores a random analytics identifier (not linked to your identity) and a cached auth token for fallback purposes. Cleared when you uninstall the extension.</li>
              <li><strong>Firebase session cookies</strong> — managed by Firebase Authentication within the extension context only; not set on third-party websites.</li>
            </ul>
            <p>We do not use tracking pixels, third-party advertising cookies, or cross-site tracking technologies.</p>
          </section>

          {/* ── Security ─────────────────────────────────────────────── */}
          <section id={slug('Data Security')}>
            <h2 data-section="Data Security">{t('account:privacy.headings.security')}</h2>
            <p>We implement reasonable technical and organizational safeguards to protect your information, including:</p>
            <ul>
              <li>HTTPS encryption for all data in transit between the extension and our servers</li>
              <li>Firebase Authentication tokens verified server-side on every API request</li>
              <li>Access tokens for connected services (Google, Todoist) encrypted at rest with AES-256-GCM and never sent to your device</li>
              <li>Database access restricted to authenticated backend processes</li>
              <li>Stripe's PCI-DSS compliant infrastructure for payment data</li>
            </ul>
            <p>No method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee absolute security. In the event of a data breach that affects your rights, we will notify you as required by applicable law.</p>
          </section>

          {/* ── Changes ──────────────────────────────────────────────── */}
          <section id={slug('Changes to This Policy')}>
            <h2 data-section="Changes to This Policy">{t('account:privacy.headings.changes')}</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
            <ul>
              <li>Updating the "Last updated" date at the top of this page</li>
              <li>Sending an email to the address associated with your account for material changes</li>
              <li>Displaying a notice in the extension for significant changes</li>
            </ul>
            <p>Your continued use of the Service after the effective date of any changes constitutes acceptance of the revised Policy. If you disagree with any changes, please stop using the Service and contact us to delete your account.</p>
          </section>

          {/* ── Contact ──────────────────────────────────────────────── */}
          <section id={slug('Contact Us')}>
            <h2 data-section="Contact Us">{t('account:privacy.headings.contact')}</h2>
            <p>For privacy-related questions, requests, or complaints:</p>
            <div className="privacy-contact-block">
              <div><strong>Email</strong></div>
              <div><a href="mailto:hello@rhemn.com">hello@rhemn.com</a></div>
              <div><strong>Subject line</strong></div>
              <div>Include "Privacy Request" in the subject so we route it correctly.</div>
              <div><strong>Response time</strong></div>
              <div>We acknowledge all requests within 10 business days.</div>
            </div>
            <p style={{ marginTop: '1.5rem', color: 'var(--privacy-muted)' }}>
              <em>Rhemn is an independent product. This privacy policy does not constitute legal advice. If you have questions about your specific legal rights, please consult a qualified attorney in your jurisdiction.</em>
            </p>
          </section>

        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
